import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Car, FileText, Home, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { SPACE_CATEGORY_LABELS } from "@/lib/offer";

const INITIAL_FORM = {
  name: "", phone: "", whatsapp: "", email: "", nationality: "", city: "", country: "RDC",
  date: "", end_date: "", time: "10:00", visit_type: "physical", zone: "zone_a",
  notes: "", guests: "", duration: "", company: "", budget: "", event_type: "",
  service_address: "", frequency: "ponctuelle", license_confirmed: false,
};

const rememberTrackedRequest = (request, listing) => {
  if (!request?.number || !request?.tracking_token) return;
  try {
    const current = JSON.parse(localStorage.getItem("tw_requests") || "[]");
    const record = {
      ...request,
      listing_title: request.listing_title || listing?.title || "",
      sector: listing?.sector || "",
      category: listing?.category || "",
    };
    localStorage.setItem("tw_requests", JSON.stringify([record, ...current.filter((item) => item.number !== record.number)]));
    window.dispatchEvent(new Event("tw:requests-updated"));
  } catch {
    // Le suivi reste disponible côté Owner même si le stockage du navigateur est indisponible.
  }
};

export default function BookingDialog({ open, onOpenChange, listing, cta = "visit", actionLabel = "" }) {
  const { t } = useI18n();
  const [pricing, setPricing] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);

  const isAuto = listing?.sector === "auto";
  const isService = listing?.sector === "services";
  const isBookableSpace = Boolean(SPACE_CATEGORY_LABELS[listing?.category]);
  const isVisit = cta === "visit" && !isBookableSpace;
  const isAutoTest = isVisit && isAuto;
  const isPropertyVisit = isVisit && !isAuto;
  const isDates = cta === "dates";
  const isQuote = ["quote", "quote_date"].includes(cta);
  const needsSchedule = isVisit || ["meeting", "quote", "quote_date", "availability"].includes(cta);
  const isRequestAction = !isVisit || isBookableSpace;

  useEffect(() => {
    if (open && isVisit) api.get("/pricing").then((response) => setPricing(response.data)).catch(() => {});
    if (open && isAutoTest) setForm((current) => ({ ...current, visit_type: current.visit_type === "physical" ? "essai" : current.visit_type }));
    if (!open) setForm(INITIAL_FORM);
  }, [open, isVisit, isAutoTest]);

  const copy = useMemo(() => {
    if (isAutoTest) return ["Réserver un essai", "Choisissez le type d’essai, la date et l’heure. Aucun frais de déplacement n’est ajouté."];
    if (isPropertyVisit) return ["Réserver une visite", "Choisissez votre mode de visite, votre zone, la date et l’heure souhaitées."];
    if (isService || cta === "quote") return ["Demander un devis", "Décrivez le service, l’adresse d’intervention et la date souhaitée."];
    return ({
      investment_file: ["Demander le dossier d’investissement", "Présentez votre intérêt afin que Team Work qualifie votre demande et organise l’accès au dossier."],
      meeting: ["Organiser une rencontre", "Présentez votre intérêt et vos disponibilités pour organiser une rencontre."],
      quote_date: ["Demander un devis pour cette date", "Indiquez la date, l’heure, la capacité et la durée de votre événement."],
      availability: ["Vérifier les disponibilités", "Indiquez la date, la capacité et la durée souhaitées."],
      dates: ["Demander cette location", "Indiquez la période de location et vos coordonnées."],
      contact: ["Contacter Team Work", "Laissez vos coordonnées et précisez l’information recherchée."],
    })[cta] || [actionLabel || "Faire une demande", "Décrivez précisément votre besoin."];
  }, [actionLabel, cta, isAutoTest, isPropertyVisit, isService]);

  const HeaderIcon = isAutoTest ? Car : isPropertyVisit ? Home : isService ? Wrench : isQuote ? FileText : CalendarDays;

  const visitKey = useMemo(() => {
    if (!listing) return null;
    const category = (listing.category || "").toLowerCase();
    if (listing.sector === "real_estate") {
      if (category === "villa") return "villa";
      if (category === "maison") return "maison";
      if (category === "immeuble") return "immeuble";
      return "appartement";
    }
    if (category === "moto") return "moto";
    if (category === "suv") return "suv";
    if (["camion", "engin"].includes(category)) return "camion";
    return "voiture";
  }, [listing]);

  const visitFee = pricing.find((item) => item.scope === "visit" && item.key === visitKey)?.price || 0;
  const zones = pricing.filter((item) => item.scope === "travel");
  const travelFee = isPropertyVisit ? pricing.find((item) => item.scope === "travel" && item.key === form.zone)?.price || 0 : 0;
  const total = visitFee + travelFee;
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!listing) return;
    if (!form.name || !form.phone) {
      toast.error("Le nom et le téléphone sont requis.");
      return;
    }
    if (needsSchedule && (!form.date || !form.time)) {
      toast.error("Choisissez une date et une heure.");
      return;
    }
    if (isDates && (!form.date || !form.end_date || form.end_date <= form.date)) {
      toast.error("Choisissez une période de location valide.");
      return;
    }
    if (isAutoTest && form.visit_type === "essai" && !form.license_confirmed) {
      toast.error("Confirmez que le conducteur possède un permis valide.");
      return;
    }

    setLoading(true);
    try {
      if (isRequestAction) {
        const requestTypes = {
          investment_file: "dossier_investissement", meeting: "demande_rencontre",
          quote_date: "devis_evenement", quote: "devis_service",
          availability: "demande_disponibilite", dates: "demande_location", contact: "demande_contact",
        };
        const requestType = isBookableSpace ? `disponibilite_${listing.category}` : (requestTypes[cta] || (isService ? "devis_service" : "demande_annonce"));
        const { data } = await api.post("/requests", {
          request_type: requestType, source: `listing_${cta}`,
          name: form.name, phone: form.phone, whatsapp: form.whatsapp || form.phone,
          email: form.email, city: form.city, listing_id: listing.id,
          message: form.notes || `${copy[0]} — ${listing.title}`,
          details: {
            action: cta, annonce: listing.title, date: form.date, date_fin: form.end_date,
            heure: form.time, duree: form.duration, nombre_personnes: form.guests,
            entreprise: form.company, budget: form.budget, type_evenement: form.event_type,
            adresse_intervention: form.service_address, frequence: form.frequency,
          },
        });
        rememberTrackedRequest(data, listing);
        toast.success(`Demande envoyée · ${data.number}. Suivez-la dans Mes demandes.`);
        onOpenChange(false);
        return;
      }

      const { data } = await api.post("/visits/reserve", {
        listing_id: listing.id, date: form.date, time: form.time, visit_type: form.visit_type,
        zone: isPropertyVisit ? form.zone : "", name: form.name, phone: form.phone,
        whatsapp: form.whatsapp || form.phone, email: form.email, nationality: form.nationality,
        city: form.city, country: form.country, source: isAutoTest ? "website_test_drive" : "website_visit",
        notes: [isAutoTest && form.license_confirmed ? "Permis valide confirmé." : "", form.notes].filter(Boolean).join(" "),
      });
      rememberTrackedRequest(data.request, listing);
      toast.success(`${isAutoTest ? "Essai demandé" : "Visite demandée"}${data.request?.number ? ` · ${data.request.number}` : ""}. Suivi disponible dans Mes demandes.`);
      onOpenChange(false);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || t("booking.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto rounded-3xl p-0" data-testid="booking-dialog">
        <div className="bg-brand-navy px-5 py-5 text-white sm:px-6">
          <DialogHeader>
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-orange text-white">
              <HeaderIcon className="h-5 w-5" />
            </div>
            <DialogTitle className="font-display text-xl text-white sm:text-2xl">{copy[0]}</DialogTitle>
            <DialogDescription className="text-sm leading-5 text-white/70">{copy[1]}</DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="space-y-5 px-5 pb-5 sm:px-6 sm:pb-6">
          <section className="pt-5">
            <div className="mb-3 text-[10px] font-extrabold uppercase tracking-[.2em] text-brand-orange">Vos coordonnées</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Nom complet *"><Input data-testid="booking-name" value={form.name} onChange={(event) => update("name", event.target.value)} required /></Field>
              <Field label="Téléphone *"><Input type="tel" data-testid="booking-phone" value={form.phone} onChange={(event) => update("phone", event.target.value)} required /></Field>
              <Field label="WhatsApp *"><Input type="tel" data-testid="booking-whatsapp" value={form.whatsapp} onChange={(event) => update("whatsapp", event.target.value)} required /></Field>
              <Field label="E-mail *"><Input type="email" data-testid="booking-email" value={form.email} onChange={(event) => update("email", event.target.value)} required /></Field>
            </div>
          </section>

          <section className="rounded-2xl bg-slate-50 p-4">
            <div className="mb-3 text-[10px] font-extrabold uppercase tracking-[.2em] text-brand-orange">Votre demande</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {isAutoTest && <>
                <div className="sm:col-span-2">
                  <Label>Type de rendez-vous</Label>
                  <Select value={form.visit_type} onValueChange={(value) => update("visit_type", value)}>
                    <SelectTrigger data-testid="booking-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="essai">Essai routier</SelectItem>
                      <SelectItem value="inspection">Inspection du véhicule</SelectItem>
                      <SelectItem value="virtual">Présentation vidéo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.visit_type === "essai" && <label className="sm:col-span-2 flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-brand-navy">
                  <input type="checkbox" className="mt-1" checked={form.license_confirmed} onChange={(event) => update("license_confirmed", event.target.checked)} />
                  Je confirme que le conducteur possède un permis de conduire valide.
                </label>}
              </>}

              {isPropertyVisit && <>
                <div>
                  <Label>Mode de visite</Label>
                  <Select value={form.visit_type} onValueChange={(value) => update("visit_type", value)}>
                    <SelectTrigger data-testid="booking-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="physical">Visite sur place</SelectItem><SelectItem value="virtual">Visite vidéo</SelectItem></SelectContent>
                  </Select>
                </div>
                {form.visit_type === "physical" && <div>
                  <Label>Zone de déplacement</Label>
                  <Select value={form.zone} onValueChange={(value) => update("zone", value)}>
                    <SelectTrigger data-testid="booking-zone"><SelectValue /></SelectTrigger>
                    <SelectContent>{zones.map((zone) => <SelectItem key={zone.key} value={zone.key}>{zone.label} — ${zone.price}</SelectItem>)}</SelectContent>
                  </Select>
                </div>}
              </>}

              {cta === "investment_file" && <>
                <Field label="Entreprise / organisation *"><Input value={form.company} onChange={(event) => update("company", event.target.value)} required /></Field>
                <Field label="Budget ou capacité d’investissement *"><Input value={form.budget} onChange={(event) => update("budget", event.target.value)} placeholder="Ex : 500 000 USD" required /></Field>
              </>}

              {cta === "meeting" && <Field label="Entreprise / organisation *"><Input value={form.company} onChange={(event) => update("company", event.target.value)} required /></Field>}

              {(cta === "quote_date" || isBookableSpace) && <>
                <Field label="Type d’événement *"><Input value={form.event_type} onChange={(event) => update("event_type", event.target.value)} placeholder="Mariage, conférence…" required /></Field>
                <Field label="Nombre de personnes *"><Input type="number" min="1" value={form.guests} onChange={(event) => update("guests", event.target.value)} required /></Field>
                <Field label="Durée souhaitée *"><Input value={form.duration} onChange={(event) => update("duration", event.target.value)} placeholder="Ex : 6 heures" required /></Field>
              </>}

              {(isService || cta === "quote") && <>
                <div className="sm:col-span-2"><Field label="Adresse d’intervention *"><Input value={form.service_address} onChange={(event) => update("service_address", event.target.value)} placeholder="Commune, quartier, avenue ou repère" required /></Field></div>
                <Field label="Fréquence">
                  <Select value={form.frequency} onValueChange={(value) => update("frequency", value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="ponctuelle">Intervention ponctuelle</SelectItem><SelectItem value="reguliere">Service régulier</SelectItem><SelectItem value="urgence">Urgence</SelectItem></SelectContent>
                  </Select>
                </Field>
              </>}

              {needsSchedule && <>
                <Field label="Date souhaitée *"><Input type="date" data-testid="booking-date" value={form.date} onChange={(event) => update("date", event.target.value)} min={new Date().toISOString().slice(0, 10)} required /></Field>
                <Field label="Heure souhaitée *"><Input type="time" data-testid="booking-time" value={form.time} onChange={(event) => update("time", event.target.value)} required /></Field>
              </>}

              {isDates && <>
                <Field label="Date de début *"><Input type="date" value={form.date} onChange={(event) => update("date", event.target.value)} min={new Date().toISOString().slice(0, 10)} required /></Field>
                <Field label="Date de fin *"><Input type="date" value={form.end_date} onChange={(event) => update("end_date", event.target.value)} min={form.date || new Date().toISOString().slice(0, 10)} required /></Field>
              </>}

              <div className="sm:col-span-2">
                <Label>Précisions *</Label>
                <Textarea data-testid="booking-notes" rows={3} value={form.notes} onChange={(event) => update("notes", event.target.value)} placeholder={isAutoTest ? "Modèle à essayer, questions, disponibilité…" : isService ? "Surface, quantité, contraintes, résultat attendu…" : "Ajoutez les informations utiles à votre demande…"} required />
              </div>
            </div>
          </section>

          {isVisit && <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-[10px] font-extrabold uppercase tracking-[.2em] text-slate-500">Récapitulatif des frais</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>{isAutoTest ? "Frais d’essai" : "Frais de visite"}</span><strong>${visitFee}</strong></div>
              {isPropertyVisit && form.visit_type === "physical" && <div className="flex justify-between"><span>Frais de déplacement</span><strong>${travelFee}</strong></div>}
              <div className="flex justify-between border-t border-slate-200 pt-2 font-display text-lg font-extrabold text-brand-navy"><span>Total</span><span data-testid="booking-total">${isPropertyVisit && form.visit_type === "virtual" ? visitFee : total}</span></div>
            </div>
          </div>}

          <DialogFooter className="grid grid-cols-2 gap-2 sm:flex">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={loading} className="btn-primary rounded-xl" data-testid="booking-submit">
              {loading ? "Envoi…" : isAutoTest ? "Réserver l’essai" : isPropertyVisit ? "Réserver la visite" : isQuote ? "Demander le devis" : "Envoyer la demande"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label>{label}</Label>{children}</div>;
}
